import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { AiOutlineLogin } from 'react-icons/ai';

const AUTHORIZED_EMAILS = [
  'umdevbr@gmail.com',
  'fndo.xyz@gmail.com',
  'fcbertoo@gmail.com',
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');

  .login-page {
    min-height: 100vh;
    background: linear-gradient(150deg, #0d2240 0%, #0a1628 40%, #080f1c 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', sans-serif;
    padding: 20px;
  }

  .login-card {
    background: #0f1e35;
    border: 1px solid rgba(0,173,181,0.22);
    border-radius: 16px;
    padding: 48px 40px 36px;
    width: 100%;
    max-width: 380px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0;
  }

  @media (max-width: 480px) {
    .login-card { padding: 36px 24px 28px; }
  }

  .login-logo {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: #00adb5;
    margin-bottom: 6px;
  }

  .login-title {
    font-size: 18px;
    font-weight: 600;
    color: #f0f4f8;
    margin-bottom: 6px;
  }

  .login-sub {
    font-size: 13px;
    color: rgba(240,244,248,0.45);
    margin-bottom: 32px;
    line-height: 1.5;
  }

  .login-icon-wrap {
    width: 64px; height: 64px;
    border-radius: 50%;
    background: rgba(0,173,181,0.1);
    border: 1px solid rgba(0,173,181,0.22);
    display: flex; align-items: center; justify-content: center;
    font-size: 28px;
    color: #00adb5;
    margin-bottom: 28px;
    cursor: pointer;
    transition: background 0.2s, transform 0.2s;
  }
  .login-icon-wrap:hover {
    background: rgba(0,173,181,0.18);
    transform: scale(1.06);
  }

  .login-btn-google {
    width: 100%;
    background: transparent;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px;
    color: #f0f4f8;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    padding: 12px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: background 0.2s, border-color 0.2s;
    margin-bottom: 14px;
  }
  .login-btn-google:hover:not(:disabled) {
    background: rgba(255,255,255,0.05);
    border-color: rgba(255,255,255,0.25);
  }
  .login-btn-google:disabled { opacity: 0.55; cursor: not-allowed; }

  .login-message {
    font-size: 13px;
    border-radius: 8px;
    padding: 10px 14px;
    width: 100%;
    line-height: 1.5;
  }
  .login-message--error {
    background: rgba(255,87,34,0.1);
    border: 1px solid rgba(255,87,34,0.3);
    color: #ff7043;
  }
  .login-message--success {
    background: rgba(0,173,181,0.1);
    border: 1px solid rgba(0,173,181,0.22);
    color: #00adb5;
  }

  .login-spinner {
    width: 18px; height: 18px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.2);
    border-top-color: #fff;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export default function Login() {
  const navigate  = useNavigate();
  const auth      = getAuth();

  const [loading,  setLoading]  = useState(true);
  const [signing,  setSigning]  = useState(false);
  const [message,  setMessage]  = useState({ text: '', type: '' });

  // Se já estiver logado e autorizado, redireciona direto
  useEffect(() => {
    console.log('Login montado');
    const unsub = onAuthStateChanged(auth, (user) => {
      console.log('Auth state:', user?.email, '| autorizado:', user ? AUTHORIZED_EMAILS.includes(user.email) : false);
      if (user && AUTHORIZED_EMAILS.includes(user.email)) {
        console.log('Redirecionando para /Painel');
        navigate('/Painel', { replace: true });
      } else {
        console.log('Não autorizado ou não logado, exibindo tela');
        setLoading(false);
      }
    });
    return () => { console.log('Login desmontado'); unsub(); };
  }, []);

  const handleGoogleLogin = async () => {
    setSigning(true);
    setMessage({ text: '', type: '' });
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const email  = result.user.email;

      if (AUTHORIZED_EMAILS.includes(email)) {
        setMessage({ text: 'Login bem-sucedido! Redirecionando...', type: 'success' });
        navigate('/Painel', { replace: true });
      } else {
        await signOut(auth);
        setMessage({ text: `Acesso não autorizado para ${email}.`, type: 'error' });
      }
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setMessage({ text: `Erro ao fazer login: ${err.message}`, type: 'error' });
      }
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="login-page">
          <div className="login-card">
            <div className="login-spinner" style={{ width: 36, height: 36, borderWidth: 3 }} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="login-page">
        <div className="login-card">
          <div className="login-logo">VEDO</div>
          <div className="login-title">Painel de Controle</div>
          <p className="login-sub">Acesso restrito à equipe autorizada.</p>

          <div className="login-icon-wrap" onClick={handleGoogleLogin}>
            <AiOutlineLogin />
          </div>

          <button
            className="login-btn-google"
            onClick={handleGoogleLogin}
            disabled={signing}
          >
            {signing ? (
              <><div className="login-spinner" /> Entrando...</>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Entrar com Google
              </>
            )}
          </button>

          {message.text && (
            <div className={`login-message login-message--${message.type}`}>
              {message.text}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
